import { useContext, useEffect, useRef, useState } from "react"
import "./styles.less"
import { fetchSearchResults, getLocations } from "src/services/search";
import { LoadingIcon } from "shared-components/Components/CustomIcons/LoadingIcon/LoadingIcon";
import { LocationOutlinedIcon } from "shared-components/Components/CustomIcons/LocationOutlineIcon/web";
import { SearchIcon } from "shared-components/Components/CustomIcons/SearchIcon/web";
import { useHomeFilters } from "shared-components/ViewFragments/Home/HomeFilters/HomeFilters.hook";
import { firstLetterToUppercase, formattedLocationsFilter, getFormattedFilters, getOptionLabel } from "src/utils/Functions";
import { encodeHashUrl } from "@Utils/Functions";
import { useRouter } from "next/router";
import useLocationExtraData from "src/hooks/locationExtraData/useLocationExtraData";
import { useSearchResultsFilters } from "shared-components/ViewFragments/SearchResults/Sider/Filters/SearchResultsFilters.hook";
import { LocationData } from "./types";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { useRecentSearches } from "shared-components/Components/Filters/KeywordLocation/useRecentSearches";
import { RecientLocations } from "shared-components/Components/Filters/KeywordLocation/RecientsLocations";
import { HomeIcon } from "src/Components/Inmobiliarias/Perfil/perfilComponents";
import { PropertyData } from "src/contexts/PropertyData/PropertyDataProvider";
import HouseIcon from "shared-components/Components/CustomIcons/HouseIcon/web";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";

type LocationSearchProps = {
    showSearchBtn: boolean,
    isFromHome?: boolean
}

//evaluar sacar el isFromHome porque ahora este componente es SOLO de la home

export const LocationSearch = ({ showSearchBtn, isFromHome } : LocationSearchProps) => {
	const { filters, filtersTags, changeFilters, search } = useHomeFilters();
    const { changeFiltersAndUpdateURL } = useSearchResultsFilters();
	const { setLocationText, locationText } = useLocationExtraData();
    const { translations, country_code, main_domain } = useContext(ConfigStateContext);
    const screen = useBreakpoint();
    const { 
        recentSearches,
        recentLocations,
        recentKeywords,
        recentReferences,
        addRecentSearch,
    } = useRecentSearches();
	const router = useRouter();
    const optionRefs = useRef([]);
    
    const [options, setOptions] = useState<LocationData[]>([]);
    const [resultByRef, setResultByRef] = useState<PropertyData | null>(null);
    const [isOnFocus, setIsOnFocus] = useState<boolean>(false);
    const [isSearchingOption, setIsSearchingOption] = useState<boolean>(false);
	const [debounceTimeOut, setDebounceTimeOut] = useState(null);
	const [text, setText] = useState<string>(locationText);
    const [showOptions, setShowOptions] = useState<boolean>(false);
    const [optionsHovered, setOptionsHovered] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState(-1);
    const containerRef = useRef(null);

    const textLocationTransform = (location:LocationData)=>{
        let locationTransformed
        if(location?.name){
            locationTransformed = location
        }
        else {
            locationTransformed = {...location,name:""}
        }
        return getOptionLabel(locationTransformed)
                .replace(/<br\s*\/?>/gi, ', ')
                .replace(/<\/?[^>]+>/g, '')
                .replace(/\s+,/g, ',')
                .replace(/\s+/g, ' ')
                .trim() ?? "";
    }

    const clearLocation = () => {
		setLocationText("");
		setText("");
		setShowOptions(false);
		setOptions([]);
	};

    const handleSelectReference = (property: PropertyData) => {
        addRecentSearch({
            type: 'reference',
            id: property.id,
            code: property.code,
            title: property.title,
            property_type: {
            name: property.property_type.name,
            id: property.property_type.id,
            },
            link: property.link,
        });
        
        if (property.link) {
            window.location.href = property.link;
        }
    };

    const handleSearchChange = (evt) => {
        const { value } = evt.target;
        const valueCleaned = value.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ˜´]/g, '');

        setIsSearchingOption(true);
        setText(valueCleaned);
        setResultByRef(null); // Resetear resultado anterior
        if( valueCleaned === "" || valueCleaned.length === 0) {
            setOptions([]);
            setShowOptions(false);
        }

        if (debounceTimeOut) {
            clearTimeout(debounceTimeOut);
        }

        setDebounceTimeOut(
            setTimeout(async () => {
                try {
                    if(country_code!=='CO'){
                        // 1. Búsqueda por referencia primero
                        const refSearch = await fetchSearchResults(
                            {
                                rows: 100,
                                params: { code: valueCleaned },
                                page: 1,
                                source: 10,
                            },
                            main_domain
                        );

                        if (refSearch?.property) {
                            setResultByRef(refSearch.property);
                            setShowOptions(true)
                            setIsSearchingOption(false);
                            return;
                        }
                    }

                    // 2. Si no encontró por referencia, buscar ubicaciones
                    const locationsData = await getLocations(valueCleaned);
                    setOptions(locationsData.searchLocation);
                    setShowOptions(true);
                    
                } catch (e) {
                    console.error("Error en búsqueda:", e);
                    // Fallback a búsqueda normal si falla la de referencia
                    try {
                        const locationsData = await getLocations(valueCleaned);
                        setOptions(locationsData.searchLocation);
                        setShowOptions(true);
                    } catch (error) {
                        console.error("Error en búsqueda de ubicaciones:", error);
                    }
                } finally {
                    setIsSearchingOption(false);
                }
            }, 500)
        );
    };

    const handleOnBlur = () => {
        if (!optionsHovered && screen.md) {
            setIsOnFocus(false);
            setShowOptions(false);
        }
    }

    const handleOnFocus = () => {
        setIsOnFocus(true);
        setLocationText("");
        setText("");
        setShowOptions(false);
        setOptions([]);
    }

    const handleClearSearch = () => {
        clearLocation()
		if(!isFromHome){
            changeFiltersAndUpdateURL({
                locations: null,
            })
        }
    }

    const handleSearchKeyword = (value) => {
        changeFilters({
            searchstring: { text: "Palabra Clave: " + value, value: value },
        });

        addRecentSearch({type: 'keyword', name: value});
        setText(value)
        setIsOnFocus(false);
    }

    const handleSearch = (option) => {
        if (!!options.length || option) {
            const optionSelected = option ?? options[0];
            const label = textLocationTransform(optionSelected)

            addRecentSearch({type:"location", ...optionSelected});
            setLocationText(label)
            setText(label)
            changeFilters({
                locations: [
                    {
                        text: "locations",
                        value: formattedLocationsFilter(optionSelected),
                    },
                ]
            });
            search.send({
                variables: {
                    params: getFormattedFilters({...filters, locations: [optionSelected]})
                }
            });
            setIsOnFocus(false);
            setShowOptions(false);
            setOptions([]);
        } else if (text !== "") {
            handleSearchKeyword(text);
        } else {
            search.send({
                variables: {
                    params: getFormattedFilters({...filters})
                }
            });
        }
    }

    const handleKeyDown = (event) => {
        if (showOptions) {
            if (event.key === "ArrowDown") {
                setSelectedOption((prevIndex) => 
                    prevIndex < options.length - 1 ? prevIndex + 1 : 0
                );
            } else if (event.key === "ArrowUp") {
                setSelectedOption((prevIndex) =>
                    prevIndex > 0 ? prevIndex - 1 : options.length - 1
                );
            } else if (event.key === "Enter" && selectedOption >= 0) {
                event.preventDefault();

                handleSearch(options[selectedOption]);
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOnFocus(false);
                setShowOptions(false);
            }
        };

        if (showOptions && !screen.md) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }
        else if((!!recentLocations.length || !!recentKeywords.length  || !!recentReferences) && !text?.length && isOnFocus){
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showOptions, screen.md, isOnFocus, recentLocations, recentKeywords, recentReferences]);

    useEffect(() => {
        if (search.response.data && !search.response.loading) {
            window.location.href = search.response.data.searchUrl.url;
        }
    }, [search.response.data]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [options, selectedOption, showOptions]);

    useEffect(() => {
        if (selectedOption >= 0 && optionRefs.current[selectedOption]) {
            optionRefs.current[selectedOption].scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [selectedOption]);

    useEffect(() => {
      clearLocation();
    }, [])

    const ResultsWithRef = () => {
        if (!resultByRef) return null;

        return (
            <li
            className="option"
            onClick={() => handleSelectReference(resultByRef)}
            >
            <span className="location-icon">
                <HouseIcon />
            </span>
            <span className="location-text">
                <span className="text">{resultByRef.title}</span>
                <span className="group">{resultByRef.property_type.name}</span>
            </span>
            </li>
        );
    };

    const DropdownElements = () => {
        if(showOptions) {
            return (
                <div className="location-search__option-container"
                        ref={containerRef}
                        onMouseEnter={() => setOptionsHovered(true)}
                        onMouseLeave={() => setOptionsHovered(false)}
                    >
                        <ul className="option-list">
                            {country_code !== 'CO' && resultByRef && <ResultsWithRef />}
                            {options.length > 0 && options.map((option, index) => {
                                const label = getOptionLabel(option);
                                const group = translations[option.type.toLowerCase()];
                                const isSelected = index === selectedOption;

                                return (
                                    <li
                                        className={`option ${isSelected ? "active" : ""}`}
                                        onClick={() => handleSearch(option)}
                                        onTouchEnd={() => handleSearch(option)}
                                        key={`option-${option.id}`}
                                        ref={(el) => optionRefs.current[index] = el}
                                    >
                                        <span className="location-icon"><LocationOutlinedIcon /></span>
                                        
                                        <span className="location-text">
                                            <span className="text" dangerouslySetInnerHTML={{__html: label}}></span>
                                            <span className="group">{group}</span>
                                        </span>
                                    </li>
                                )
                            })}

                            {
                                options.length === 0 && text?.length && !resultByRef && (
                                    <div
                                    className="option"
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        borderBottom: "solid 1px #dadada",
                                        padding: "10px 5px",
                                    }}
                                    onClick={() => handleSearchKeyword(text)}
                                    onTouchEnd={() => handleSearchKeyword(text)}
                                    >
                                    <div style={{ position: "relative", top: 2 }}>
                                        <SearchIcon width={20} />
                                    </div>
                                    <div
                                        style={{
                                            flexGrow: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            paddingLeft: 10,
                                            lineHeight: 1.1,
                                        }}>
                                        <span>
                                            Buscar inmuebles relacionados a{" "}
                                            <b style={{ fontWeight: "bold" }}>{text}</b>
                                        </span>
                                    </div>
                                </div>
                                )
                            }
                        </ul>
                    </div>
            )
        }
        else {
            if ((!!recentLocations.length || !!recentKeywords.length  || !!recentReferences) && !text?.length && isOnFocus){
                return (
                        <div
                            onMouseEnter={() => setOptionsHovered(true)}
                            onMouseLeave={() => setOptionsHovered(false)}
                            className="location-search__option-container"
                            ref={containerRef}
                        >
                            <RecientLocations
                                recientLocations={recentSearches}
                                handleSelectLocation={handleSearch}
                                handleSelectKeyword={handleSearchKeyword}
                                handleSelectReference={handleSelectReference}
                            />
                        </div>
                    )
            }
            else if(!isSearchingOption && !options.length && !search.response.loading && !!text?.length && isOnFocus){
                return (
                        <div 
                            className="option-keyword"
                            style={{display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: 'solid 1px #dadada', padding: '10px 5px'}}
                            onMouseEnter={() => setOptionsHovered(true)}
                            onMouseLeave={() => setOptionsHovered(false)}
                            onClick={() => handleSearchKeyword(text)}
                            ref={containerRef}
                        >
                            <div style={{position: 'relative', top: 2}}>
                                <SearchIcon width={20}/>
                            </div>
                            <div style={{flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingLeft: 10, lineHeight: 1.1}}>
                                <span>Buscar inmuebles relacionados a <b style={{fontWeight: 'bold'}}>{text}</b></span>
                            </div>
                        </div>
                    )
            }
        }
        return <></>
    }
    
    return (
        <div className="location-search__container">
            <div className="location-search__input-container">
                <input
                    type="text"
                    className="location-search__input"
                    placeholder={`${firstLetterToUppercase(translations.busca)} por ubicación o palabra clave`}
                    value={text ?? ""}
                    onFocus={handleOnFocus}
                    onChange={handleSearchChange}
                    onBlur={handleOnBlur}
                />

                {( text && text.length || (filters.locations?.length)) ?
                    <button className="location-search__clear" onClick={handleClearSearch}>
                        x
                    </button> :
                    null
                }

                {isOnFocus &&
                    <div className="location-search__label">
                        {isSearchingOption ?
                            <span className="location-search__label-loading">
                                <LoadingIcon classes="loading-icon" />
                                Cargando
                            </span> :
                            <span>Escribe una ubicación o palabra clave</span>
                        }
                    </div>
                }

                {/* {!isSearchingOption && !options.length && !search.response.loading && !!text?.length && isOnFocus && (
                    <div 
                        className="option-keyword"
                        style={{display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: 'solid 1px #dadada', padding: '10px 5px'}}
                        onMouseEnter={() => seOptionsHovered(true)}
                        onMouseLeave={() => seOptionsHovered(false)}
                        onClick={() => handleSearchKeyword(text)}
                    >
                        <div style={{position: 'relative', top: 2}}>
                            <SearchIcon width={20}/>
                        </div>
                        <div style={{flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingLeft: 10, lineHeight: 1.1}}>
                            <span>Buscar inmuebles relacionados a <b style={{fontWeight: 'bold'}}>{text}</b></span>
                        </div>
                    </div>
                )} */}

                {/* {(!!recentLocations.length || !!recentReferences || recentKeywords.length) && !text?.length && isOnFocus ? (
                    <div
                        onMouseEnter={() => seOptionsHovered(true)}
                        onMouseLeave={() => seOptionsHovered(false)}
                        className="location-search__option-container"
                    >
                        <RecientLocations
                            recientLocations={recentSearches}
                            handleSelectLocation={handleSearch}
                            handleSelectKeyword={handleSearchKeyword}
                            handleSelectReference={handleSelectReference}
                        />
                    </div>
                ): null} */}

                {/* {showOptions &&
                    <div className="location-search__option-container"
                        onMouseEnter={() => seOptionsHovered(true)}
                        onMouseLeave={() => seOptionsHovered(false)}
                    >
                        <ul className="option-list">
                            {country_code !== 'CO' && resultByRef && <ResultsWithRef />}
                            {options.length>0 && options.map((option, index) => {
                                const label = getOptionLabel(option);
                                const group = translations[option.type.toLowerCase()];
                                const isSelected = index === selectedOption;

                                return (
                                    <li
                                        className={`option ${isSelected ? "active" : ""}`}
                                        onClick={() => handleSearch(option)}
                                        key={`option-${option.id}`}
                                        ref={(el) => optionRefs.current[index] = el}
                                    >
                                        <span className="location-icon"><LocationOutlinedIcon /></span>
                                        
                                        <span className="location-text">
                                            <span className="text" dangerouslySetInnerHTML={{__html: label}}></span>
                                            <span className="group">{group}</span>
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                } */}
                <DropdownElements/>
            </div>

            {showSearchBtn &&
                <div className="search-button-container">
                    <button
                        className="search-button superPrimary btn-primary"
                        onClick={() => handleSearch(null)}
                    >
                        {search.response.loading ? (
                            <LoadingIcon classes="loading-icon" />
                        ) : (
                            <SearchIcon />
                        )}
                    </button>
                </div>
            }
        </div>
    )
}